import { getCurrentLanguage } from './index.js';
import { getLanguageSwitcherModel } from './languageSettings.js';

const LANGUAGE_STORAGE_KEY = 'i18nextLng';

export function getStoredLanguage(storage = globalThis.localStorage) {
  return storage.getItem(LANGUAGE_STORAGE_KEY);
}

export function clearStoredLanguage(storage = globalThis.localStorage) {
  storage.removeItem(LANGUAGE_STORAGE_KEY);
}

function renderFlagMarkup(language) {
  return `<span class="fi fi-${language.flagCode} language-switcher__flag" aria-hidden="true"></span>`;
}

const LANGUAGE_SWITCHER_STYLE_OPTIONS = {
  colorful: {
    triggerLabel: 'native',
    optionLabel: 'native',
    showTriggerFlag: true,
    showOptionFlags: true,
    showTriggerChevron: false,
  },
  simple: {
    triggerLabel: 'code',
    optionLabel: 'code',
    showTriggerFlag: false,
    showOptionFlags: false,
    showTriggerChevron: true,
  },
};

function resolveStyleOptions(options = {}) {
  const style = Object.prototype.hasOwnProperty.call(LANGUAGE_SWITCHER_STYLE_OPTIONS, options.style)
    ? options.style
    : 'colorful';

  return {
    ...LANGUAGE_SWITCHER_STYLE_OPTIONS[style],
    ...options,
    style,
  };
}

function getLanguageLabel(language, labelMode = 'native') {
  if (!language) {
    return '';
  }

  return labelMode === 'code' ? language.code.toUpperCase() : language.nativeLabel;
}

function renderTriggerMarkup(selectedLanguage, options) {
  if (!selectedLanguage) {
    return '';
  }

  const flagMarkup = options.showTriggerFlag === false ? '' : renderFlagMarkup(selectedLanguage);
  const chevronMarkup = options.showTriggerChevron
    ? '<span class="language-switcher__chevron" aria-hidden="true"></span>'
    : '';
  const label = getLanguageLabel(selectedLanguage, options.triggerLabel);

  return `
    <span class="language-switcher__trigger-content">
      ${flagMarkup}
      <span class="language-switcher__label">${label}</span>
      ${chevronMarkup}
    </span>
  `;
}

function renderOptionsMarkup(model, options) {
  return model.languages
    .map((language) => {
      const isActive = model.selectedLanguage && language.code === model.selectedLanguage.code;
      const activeClassName = isActive ? ' language-switcher__option--active' : '';
      const flagMarkup = options.showOptionFlags === false ? '' : renderFlagMarkup(language);
      const label = getLanguageLabel(language, options.optionLabel);

      return `
        <li class="language-switcher__item">
          <button
            type="button"
            class="language-switcher__option${activeClassName}"
            data-language-code="${language.code}"
            aria-pressed="${String(isActive)}"
          >
            ${flagMarkup}
            <span class="language-switcher__option-label">${label}</span>
          </button>
        </li>
      `;
    })
    .join('');
}

export function createLanguageSwitcher(mountElement, languageCodes = [], options = {}) {
  if (!mountElement) {
    return null;
  }

  const styleOptions = resolveStyleOptions(options);
  const rootElement = mountElement;
  const documentRef = rootElement.ownerDocument;
  const windowRef = documentRef.defaultView;

  let isOpen = false;
  let model = getLanguageSwitcherModel(languageCodes, getCurrentLanguage() || 'en');

  function focusActiveOption() {
    if (!windowRef) {
      return;
    }

    windowRef.requestAnimationFrame(() => {
      const activeOption =
        rootElement.querySelector('.language-switcher__option--active') ||
        rootElement.querySelector('.language-switcher__option');

      if (activeOption && typeof activeOption.focus === 'function') {
        activeOption.focus();
      }
    });
  }

  function render() {
    model = getLanguageSwitcherModel(languageCodes, getCurrentLanguage() || model.selectedLanguage?.code || 'en');
    rootElement.classList.add('language-switcher');
    rootElement.classList.remove('language-switcher_style_colorful', 'language-switcher_style_simple');
    rootElement.classList.add(`language-switcher_style_${styleOptions.style}`);
    rootElement.hidden = !model.isVisible;

    if (!model.isVisible) {
      rootElement.innerHTML = '';
      return;
    }

    rootElement.innerHTML = `
      <button
        type="button"
        class="language-switcher__trigger"
        aria-haspopup="dialog"
        aria-expanded="${String(isOpen)}"
        aria-label="Language: ${model.selectedLanguage.nativeLabel}"
      >
        ${renderTriggerMarkup(model.selectedLanguage, styleOptions)}
      </button>
      <div class="language-switcher__overlay"${isOpen ? '' : ' hidden'} role="dialog" aria-modal="true">
        <div class="language-switcher__panel">
          <ul class="language-switcher__list">
            ${renderOptionsMarkup(model, styleOptions)}
          </ul>
        </div>
      </div>
    `;

    if (isOpen) {
      focusActiveOption();
    }
  }

  function closeOverlay() {
    if (!isOpen) {
      return;
    }

    isOpen = false;
    render();

    const trigger = rootElement.querySelector('.language-switcher__trigger');
    if (trigger && typeof trigger.focus === 'function') {
      trigger.focus();
    }
  }

  function handleLanguagePointerDown(event) {
    const target = event.target && typeof event.target.closest === 'function' ? event.target : null;
    const languageButton = target ? target.closest('[data-language-code]') : null;

    if (!languageButton) {
      return;
    }

    const selectedLanguage = languageButton.dataset.languageCode;
    if (!selectedLanguage) {
      return;
    }

    event.preventDefault();

    if (selectedLanguage === getCurrentLanguage()) {
      closeOverlay();
      return;
    }

    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, selectedLanguage);
    window.location.reload();
  }

  function handleRootClick(event) {
    const target = event.target && typeof event.target.closest === 'function' ? event.target : null;
    const languageButton = target ? target.closest('[data-language-code]') : null;

    if (languageButton) {
      return;
    }

    const trigger = target ? target.closest('.language-switcher__trigger') : null;
    if (trigger) {
      isOpen = true;
      render();
      return;
    }

    const overlay = rootElement.querySelector('.language-switcher__overlay');
    if (event.target === overlay) {
      closeOverlay();
    }
  }

  function handleDocumentKeydown(event) {
    if (event.key === 'Escape') {
      closeOverlay();
    }
  }

  rootElement.addEventListener('pointerdown', handleLanguagePointerDown);
  rootElement.addEventListener('click', handleRootClick);
  documentRef.addEventListener('keydown', handleDocumentKeydown);

  render();

  return {
    destroy() {
      rootElement.removeEventListener('pointerdown', handleLanguagePointerDown);
      rootElement.removeEventListener('click', handleRootClick);
      documentRef.removeEventListener('keydown', handleDocumentKeydown);
      rootElement.innerHTML = '';
      rootElement.hidden = true;
    },
  };
}
